import React, { useState, useContext, useEffect, useRef } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs, query, where, orderBy, limit, deleteDoc, addDoc, increment, serverTimestamp } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faTimes, 
  faTrash,
  faSpinner,
  faUsers,
  faEdit,
  faLock,
  faUser,
  faCalendarAlt,
  faArrowLeft,
  faCheck,
  faUserShield,
  faInfoCircle,
  faArrowRight,
  faBuilding
} from '@fortawesome/free-solid-svg-icons';
import { liteClient } from 'algoliasearch/lite';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { createNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './AssociatedVendors.module.css';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { useNavigate } from 'react-router-dom';

// Initialize Algolia
const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

// Permission Denied Component
const PermissionDenied = () => (
  <div className={styles.permissionDenied}>
    <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
    <h2>Access Restricted</h2>
    <p>You don't have permission to view this page. This section is only available to Staff accounts.</p>
  </div>
);

const AssociatedVendors = () => {
  const [activeTab, setActiveTab] = useState('current');
  const [isVendorModalVisible, setIsVendorModalVisible] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [displayedVendors, setDisplayedVendors] = useState([]);
  const [pastVendors, setPastVendors] = useState([]);
  const [displayedPastVendors, setDisplayedPastVendors] = useState([]);
  const [hasMoreVendors, setHasMoreVendors] = useState(false);
  const [hasMorePastVendors, setHasMorePastVendors] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [vendorCount, setVendorCount] = useState(0);
  const [pastVendorCount, setPastVendorCount] = useState(0);
  const { userData } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tours, setTours] = useState([]);
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  
  // Vendor search states
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [vendorResults, setVendorResults] = useState([]);
  const [searchingVendor, setSearchingVendor] = useState(false);
  const [isMoveToPastModalVisible, setIsMoveToPastModalVisible] = useState(false);
  const [selectedVendorForMove, setSelectedVendorForMove] = useState(null);

  // States for selected vendor in modal
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  // For dates - current and past vendors
  const [sinceMonth, setSinceMonth] = useState('');
  const [sinceYear, setSinceYear] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');

  // Check if user has access based on userType
  useEffect(() => {
    if (userData) {
      setHasAccess(userData.userType === 'Staff');
    }
  }, [userData]);

  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load vendor data with subcollection pattern
  useEffect(() => {
    if (!userData?.userId) return;
    
    const loadVendorData = async () => {
      setIsProcessing(true);
      try {
        const db = getFirestoreInstance();
        
        // First get the user document to get the count and top 3 vendors
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        let initialVendors = [];
        let initialPastVendors = [];
        let totalVendorCount = 0;
        let totalPastVendorCount = 0;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          initialVendors = userData.associatedVendors || [];
          initialPastVendors = userData.pastAssociatedVendors || [];
          totalVendorCount = userData.associatedVendorsCount || 0;
          totalPastVendorCount = userData.pastAssociatedVendorsCount || 0;
          
          // Set the count states
          setVendorCount(totalVendorCount);
          setPastVendorCount(totalPastVendorCount);
        }
        
        // Set the initial displayed vendors
        setDisplayedVendors(initialVendors);
        setDisplayedPastVendors(initialPastVendors);
        
        // Check if there are more vendors than what's displayed
        setHasMoreVendors(totalVendorCount > initialVendors.length);
        setHasMorePastVendors(totalPastVendorCount > initialPastVendors.length);
        
        // Load pending requests from pendingAssociatedVendors subcollection
        const pendingVendorsRef = collection(db, 'users', userData.userId, 'pendingAssociatedVendors');
        const pendingVendorsSnapshot = await getDocs(pendingVendorsRef);
        
        if (!pendingVendorsSnapshot.empty) {
          // Process pending vendors
          const pendingVendors = await Promise.all(
            pendingVendorsSnapshot.docs.map(async (docSnapshot) => {
              const vendorData = docSnapshot.data();
              
              // Always fetch the latest data for the vendor
              if (vendorData.vendorId) {
                try {
                  const userDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                  if (userDoc.exists()) {
                    const userData = userDoc.data();
                    return {
                      id: docSnapshot.id,
                      vendorId: vendorData.vendorId,
                      fullName: userData.fullName || '',
                      userName: userData.userName || '',
                      profilePictureUrl: userData.profilePictureUrl || '',
                      userSubType: userData.userSubType || '',
                      email: userData.email || '',
                      idVerificationStatus: userData.idVerificationStatus || '',
                      status: vendorData.status || 'pending',
                      createdAt: vendorData.createdAt || new Date().toISOString(),
                      since: vendorData.since || null,
                      isPending: true, // Mark as pending for UI
                      isPast: vendorData.isPast || false
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching user data for ${vendorData.vendorId}:`, error);
                }
              }
              
              // Return the vendor data as is if user data enrichment failed
              return {
                id: docSnapshot.id,
                ...vendorData,
                isPending: true
              };
            })
          );
          
          // Add pending vendors to the display (separated by current/past)
          const currentPendingVendors = pendingVendors.filter(vendor => !vendor.isPast);
          const pastPendingVendors = pendingVendors.filter(vendor => vendor.isPast);
          
          // Update displayed vendors with pending ones at the top
          if (currentPendingVendors.length > 0) {
            setDisplayedVendors(prev => [...currentPendingVendors, ...prev]);
          }
          
          if (pastPendingVendors.length > 0) {
            setDisplayedPastVendors(prev => [...pastPendingVendors, ...prev]);
          }
        }
        
        // If we need to display vendors, fetch them from subcollection
        if (initialVendors.length > 0) {
          // Fetch all current vendors from subcollection for complete data
          const vendorsRef = collection(db, 'users', userData.userId, 'associatedVendors');
          const vendorsQuery = query(
            vendorsRef,
            where('isPast', '==', false),
            orderBy('createdAt', 'desc')
          );
          
          const vendorsSnapshot = await getDocs(vendorsQuery);
          
          if (!vendorsSnapshot.empty) {
            const allVendors = await Promise.all(
              vendorsSnapshot.docs.map(async (docSnapshot) => {
                const vendorData = docSnapshot.data();
                
                // Enrich with FRESH user data every time
                if (vendorData.vendorId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        vendorId: vendorData.vendorId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: vendorData.status || 'pending',
                        createdAt: vendorData.createdAt || new Date().toISOString(),
                        since: vendorData.since || null,
                        permissions: vendorData.permissions || {}
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${vendorData.vendorId}:`, error);
                  }
                }
                
                // Return the vendor data as is if user data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...vendorData
                };
              })
            );
            
            // Set the full vendor list
            setVendors(allVendors);
          } else {
            setVendors(initialVendors);
          }
        }
        
        // Similar logic for past vendors - fetch fresh data every time
        if (initialPastVendors.length > 0) {
          // Fetch all past vendors from subcollection for complete data
          const pastVendorsRef = collection(db, 'users', userData.userId, 'associatedVendors');
          const pastVendorsQuery = query(
            pastVendorsRef,
            where('isPast', '==', true),
            orderBy('createdAt', 'desc')
          );
          
          const pastVendorsSnapshot = await getDocs(pastVendorsQuery);
          
          if (!pastVendorsSnapshot.empty) {
            const allPastVendors = await Promise.all(
              pastVendorsSnapshot.docs.map(async (docSnapshot) => {
                const vendorData = docSnapshot.data();
                
                // Enrich with user data
                if (vendorData.vendorId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        vendorId: vendorData.vendorId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: vendorData.status || 'pending',
                        createdAt: vendorData.createdAt || new Date().toISOString(),
                        duration: vendorData.duration || null
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${vendorData.vendorId}:`, error);
                  }
                }
                
                // Return the vendor data as is if user data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...vendorData
                };
              })
            );
            
            // Set the full past vendor list
            setPastVendors(allPastVendors);
          } else {
            setPastVendors(initialPastVendors);
          }
        }
      } catch (error) {
        console.error('Error fetching vendor data:', error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    loadVendorData();
  }, [userData?.userId]);

  // Add function to navigate to vendor profile
  const navigateToUserProfile = (userName) => {
    navigate(`/${userName}`);
  };

  const handleVendorSearch = async () => {
    if (!vendorSearchQuery.trim()) return;
    
    try {
      setSearchingVendor(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: vendorSearchQuery,
        filters: 'userType:Vendor',
        hitsPerPage: 10
      }]);
  
      const formattedVendors = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isAlreadyAdded: vendors.some(vendor => vendor.vendorId === hit.objectID) ||
                       pastVendors.some(vendor => vendor.vendorId === hit.objectID)
      }));
      
      setVendorResults(formattedVendors);
    } catch (error) {
      console.error('Error searching vendors:', error);
      setVendorResults([]);
    } finally {
      setSearchingVendor(false);
    }
  };

  // Load more vendors from subcollection
  const loadMoreVendors = async () => {
    if (isLoadingMore || !userData?.userId) return;
    
    setIsLoadingMore(true);
    try {
      if (activeTab === 'current') {
        // Skip loading more if we already have all vendors loaded
        if (displayedVendors.length >= vendorCount) {
          setHasMoreVendors(false);
          return;
        }
        
        // Load the next batch of vendors
        const nextBatch = vendors.slice(
          displayedVendors.length,
          displayedVendors.length + 6
        );
        
        setDisplayedVendors(prev => [...prev, ...nextBatch]);
        
        // Check if there are more vendors to load
        setHasMoreVendors(displayedVendors.length + nextBatch.length < vendorCount);
      } else {
        // Skip loading more if we already have all past vendors loaded
        if (displayedPastVendors.length >= pastVendorCount) {
          setHasMorePastVendors(false);
          return;
        }
        
        // Load the next batch of past vendors
        const nextBatch = pastVendors.slice(
          displayedPastVendors.length,
          displayedPastVendors.length + 6
        );
        
        setDisplayedPastVendors(prev => [...prev, ...nextBatch]);
        
        // Check if there are more past vendors to load
        setHasMorePastVendors(displayedPastVendors.length + nextBatch.length < pastVendorCount);
      }
    } catch (error) {
      console.error(`Error loading more ${activeTab} vendors:`, error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle initial selection of a vendor
  const handleInitialSelection = (vendor) => {
    setSelectedVendor(vendor);
    
    // Reset form fields
    setSinceMonth('');
    setSinceYear('');
    setStartMonth('');
    setStartYear('');
    setEndMonth('');
    setEndYear('');
  };

  // Handle submission of the vendor addition form
  const handleSubmitVendor = async () => {
    if (!selectedVendor) return;
    
    // Validate form fields
    if (activeTab === 'current') {
      // Validate current association fields
      if (!sinceMonth || !sinceYear) {
        alert('Please fill in the since date');
        return;
      }
    } else {
      // Validate past association fields
      if (!startMonth || !startYear || !endMonth || !endYear) {
        alert('Please fill in all date fields');
        return;
      }
      
      // Validate date logic
      const startDate = new Date(parseInt(startYear), parseInt(startMonth) - 1);
      const endDate = new Date(parseInt(endYear), parseInt(endMonth) - 1);
      
      if (startDate > endDate) {
        alert('Start date must be before end date');
        return;
      }
    }
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Prepare vendor data
      const vendorData = {
        vendorId: selectedVendor.id,
        status: 'pending',
        isPast: activeTab === 'past',
        createdAt: serverTimestamp()
      };
      
      // Add date information
      if (activeTab === 'current') {
        vendorData.since = {
          month: sinceMonth,
          year: sinceYear
        };
      } else {
        vendorData.duration = {
          startMonth,
          startYear,
          endMonth,
          endYear
        };
      }
      
      // Format date for message
      const formattedDate = activeTab === 'current'
        ? `Since: ${sinceMonth}/${sinceYear}`
        : `From: ${startMonth}/${startYear} To: ${endMonth}/${endYear}`;
      
      const notificationMessage = `${userData.fullName || 'A staff member'} (${userData.userSubType}) wants to add you as their ${activeTab === 'current' ? '' : 'past '}vendor. ${formattedDate}`;
      
      // Create the title based on request type
      const notificationTitle = activeTab === 'current' 
        ? 'Associated Vendor Request' 
        : 'Past Associated Vendor Request';
      
      // Add to pendingAssociatedVendors subcollection
      const pendingVendorRef = collection(db, 'users', userData.userId, activeTab === 'current' ? 'pendingAssociatedVendors' : 'pendingPastAssociatedVendors');
      const pendingVendorDoc = await addDoc(pendingVendorRef, {
        ...vendorData,
        // Store entity display data for UI
        targetData: {
          fullName: selectedVendor.fullName || '',
          userName: selectedVendor.userName || '',
          profilePictureUrl: selectedVendor.profilePictureUrl || '',
          userSubType: selectedVendor.userSubType || '',
          idVerificationStatus: selectedVendor.idVerificationStatus || ''
        }
      });
      
      // Now use createNotification from NotificationService with new specific notification types
      const notificationResponse = await createNotification(selectedVendor.id, 'general', {
        type: activeTab === 'current' ? NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REQUEST : NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REQUEST,
        title: notificationTitle,
        message: notificationMessage,
        vendorId: selectedVendor.id,
        status: 'pending',
        requestId: pendingVendorDoc.id,
        createdBy: {
          userId: userData.userId,
          fullName: userData.fullName,
          userName: userData.userName,
          profilePictureUrl: userData.profilePictureUrl || ''
        },
        data: {
          requestData: {
            requestedBy: {
              userId: userData.userId,
              fullName: userData.fullName,
              userName: userData.userName,
              profilePictureUrl: userData.profilePictureUrl || '',
              userType: userData.userType || 'Staff',
              userSubType: userData.userSubType || ''
            },
            role: 'staff',
            isPast: activeTab === 'past',
            ...(activeTab === 'current' 
              ? { since: { month: sinceMonth, year: sinceYear } } 
              : { duration: { startMonth, startYear, endMonth, endYear } }
            ),
            requestDocId: pendingVendorDoc.id,
            associationId: `${userData.userId}_${selectedVendor.id}_${Date.now()}`
          }
        }
      });
      
      // Update the pending request document with the notification ID
      await updateDoc(doc(db, 'users', userData.userId, 
        activeTab === 'current' ? 'pendingAssociatedVendors' : 'pendingPastAssociatedVendors', 
        pendingVendorDoc.id), {
        notificationId: notificationResponse.id
      });
      
      // Store the enriched data for UI updates
      const enrichedVendorData = {
        id: pendingVendorDoc.id,
        ...vendorData,
        vendorId: selectedVendor.id,
        // We'll still need these for UI display
        fullName: selectedVendor.fullName || '',
        userName: selectedVendor.userName || '',
        profilePictureUrl: selectedVendor.profilePictureUrl || '',
        userSubType: selectedVendor.userSubType || '',
        email: selectedVendor.email || '',
        idVerificationStatus: selectedVendor.idVerificationStatus || '',
        isPending: true, // Mark as pending for UI
        // Store notification doc ID for direct access
        notificationId: notificationResponse.id
      };
      
      // Update the appropriate state arrays for UI
      if (activeTab === 'current') {
        setVendors(prev => [enrichedVendorData, ...prev]);
        
        // Increment the vendor count
        const newVendorCount = vendorCount + 1;
        setVendorCount(newVendorCount);
        
        // Update displayed vendors
        setDisplayedVendors(prev => [enrichedVendorData, ...prev]);
        
        // Update hasMoreVendors based on the new count
        setHasMoreVendors(newVendorCount > displayedVendors.length + 1);
      } else {
        setPastVendors(prev => [enrichedVendorData, ...prev]);
        
        // Increment the past vendor count
        const newPastVendorCount = pastVendorCount + 1;
        setPastVendorCount(newPastVendorCount);
        
        // Update displayed past vendors
        setDisplayedPastVendors(prev => [enrichedVendorData, ...prev]);
        
        // Update hasMorePastVendors based on the new count
        setHasMorePastVendors(newPastVendorCount > displayedPastVendors.length + 1);
      }
      
      // Reset search form
      setVendorSearchQuery('');
      setVendorResults([]);
      setSelectedVendor(null);
      
      // Close modal
      setIsVendorModalVisible(false);
      
      alert(`Vendor request sent successfully! Waiting for approval.`);
    } catch (error) {
      console.error('Error adding vendor:', error);
      alert('Failed to add vendor. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to remove this vendor?')) return;
    
    const vendor = activeTab === 'current' 
      ? displayedVendors.find(v => v.id === vendorId)
      : displayedPastVendors.find(v => v.id === vendorId);
    
    if (!vendor) {
      console.error('Vendor not found:', vendorId);
      return;
    }
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Handle pending request
      if (vendor.isPending) {
        // Delete from pendingAssociatedVendors subcollection
        const pendingVendorRef = doc(db, 'users', userData.userId, activeTab === 'current' ? 'pendingAssociatedVendors' : 'pendingPastAssociatedVendors', vendor.id);
        await deleteDoc(pendingVendorRef);
        
        // Delete notification
        if (vendor.notificationId) {
          await deleteDoc(doc(db, 'users', vendor.vendorId, 'notifications', vendor.notificationId));
        }
        
        // Update UI by removing this vendor
        if (activeTab === 'current') {
          setDisplayedVendors(prev => prev.filter(v => v.id !== vendor.id));
          setVendors(prev => prev.filter(v => v.id !== vendor.id));
        } else {
          setDisplayedPastVendors(prev => prev.filter(v => v.id !== vendor.id));
          setPastVendors(prev => prev.filter(v => v.id !== vendor.id));
        }
        
        alert('Pending request canceled successfully');
        setIsProcessing(false);
        return;
      }
      
      // For non-pending (accepted) vendor, handle removal
      const userRef = doc(db, 'users', userData.userId);
      
      // Check if vendor exists in the associatedVendors subcollection
      const vendorRef = doc(db, 'users', userData.userId, 'associatedVendors', vendor.id);
      const vendorDoc = await getDoc(vendorRef);
      
      if (!vendorDoc.exists()) {
        console.error('Vendor document not found in associatedVendors subcollection:', vendor.id);
        throw new Error('Vendor document not found in subcollection');
      }
      
      const vendorData = vendorDoc.data();
      
      // If we have stored otherSideDocId, use it for direct access
      const vendorUserId = vendorData.vendorId;
      const otherSideDocId = vendorData.otherSideDocId;
      
      // 1. Delete from associatedVendors subcollection
      await deleteDoc(vendorRef);
      
      // 2. If we have the ID for the other side document, delete it directly
      if (vendorUserId && otherSideDocId) {
        // Delete from vendor's vendorStaff collection
        await deleteDoc(doc(db, 'users', vendorUserId, 'vendorStaff', otherSideDocId));
      }
      
      // 3. Update main document and states
      const isPast = activeTab === 'past';
      
      if (isPast) {
        // Update both state arrays
        setPastVendors(prev => prev.filter(v => v.id !== vendor.id));
        setDisplayedPastVendors(prev => prev.filter(v => v.id !== vendor.id));
        
        // Decrement the vendor count
        const newPastVendorCount = Math.max(0, pastVendorCount - 1);
        setPastVendorCount(newPastVendorCount);
        
        // Get new top 3 past vendors for main document
        const vendorColRef = collection(db, 'users', userData.userId, 'associatedVendors');
        const top3Query = query(
          vendorColRef,
          where('isPast', '==', true),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3Snapshot = await getDocs(top3Query);
        const top3PastVendors = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const vendorData = docSnapshot.data();
            
            // Enrich with user data
            if (vendorData.vendorId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...vendorData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${vendorData.vendorId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...vendorData
            };
          })
        );
        
        // 3. Update main document with top3 and the count for past vendors
        await updateDoc(userRef, {
          pastAssociatedVendors: top3PastVendors,
          pastAssociatedVendorsCount: increment(-1)
        });
        
        // 4. Send notification about removal
        await sendRemovalNotification(vendorUserId, true);
        
        // Update hasMorePastVendors based on the new count
        setHasMorePastVendors(newPastVendorCount > top3PastVendors.length);
      } else {
        // Handle current vendors
        setVendors(prev => prev.filter(v => v.id !== vendor.id));
        setDisplayedVendors(prev => prev.filter(v => v.id !== vendor.id));
        
        // Decrement the vendor count
        const newVendorCount = Math.max(0, vendorCount - 1);
        setVendorCount(newVendorCount);
        
        // Get new top 3 vendors for main document
        const vendorColRef = collection(db, 'users', userData.userId, 'associatedVendors');
        const top3Query = query(
          vendorColRef,
          where('isPast', '==', false),
          orderBy('createdAt', 'desc'),
          limit(3)
        );
        
        const top3Snapshot = await getDocs(top3Query);
        const top3Vendors = await Promise.all(
          top3Snapshot.docs.map(async (docSnapshot) => {
            const vendorData = docSnapshot.data();
            
            // Enrich with user data
            if (vendorData.vendorId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: docSnapshot.id,
                    ...vendorData,
                    fullName: userData.fullName || '',
                    userName: userData.userName || '',
                    profilePictureUrl: userData.profilePictureUrl || '',
                    userSubType: userData.userSubType || '',
                    idVerificationStatus: userData.idVerificationStatus || ''
                  };
                }
              } catch (error) {
                console.error(`Error fetching user data for ${vendorData.vendorId}:`, error);
              }
            }
            
            return {
              id: docSnapshot.id,
              ...vendorData
            };
          })
        );
        
        // Update main document with top3 and the count
        await updateDoc(userRef, {
          associatedVendors: top3Vendors,
          associatedVendorsCount: increment(-1)
        });
        
        // Send notification about removal
        await sendRemovalNotification(vendorUserId, false);
        
        // Update hasMoreVendors based on the new count
        setHasMoreVendors(newVendorCount > top3Vendors.length);
      }
      
      // FIX: Update the vendor's main document with updated vendorStaff array
      if (vendorUserId) {
        try {
          // Get the vendor's document
          const vendorUserRef = doc(db, 'users', vendorUserId);
          const vendorUserDoc = await getDoc(vendorUserRef);
          
          if (vendorUserDoc.exists()) {
            // Get the top 3 staff from vendorStaff subcollection for the vendor
            const vendorStaffRef = collection(db, 'users', vendorUserId, 'vendorStaff');
            const staffQuery = query(
              vendorStaffRef,
              where('isPast', '==', isPast),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const staffSnapshot = await getDocs(staffQuery);
            const top3Staff = await Promise.all(
              staffSnapshot.docs.map(async (docSnapshot) => {
                const staffData = docSnapshot.data();
                
                // Enrich with user data
                if (staffData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', staffData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        ...staffData,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        idVerificationStatus: userData.idVerificationStatus || ''
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${staffData.userId}:`, error);
                  }
                }
                
                return {
                  id: docSnapshot.id,
                  ...staffData
                };
              })
            );
            
            // Update the vendor's main document with the top 3 staff
            await updateDoc(vendorUserRef, {
              [isPast ? 'pastVendorStaff' : 'vendorStaff']: top3Staff,
              [isPast ? 'pastVendorStaffCount' : 'vendorStaffCount']: increment(-1)
            });
          }
        } catch (error) {
          console.error('Error updating vendor staffList array:', error);
          // Continue even if this fails
        }
      }
      
      alert(`Vendor removed successfully`);
    } catch (error) {
      console.error('Error removing vendor:', error);
      alert('Failed to remove vendor. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Helper function to send removal notifications
  const sendRemovalNotification = async (vendorId, isPast) => {
    try {
      const db = getFirestoreInstance();
      const messagePrefix = isPast ? 'past ' : '';
      
      // Create notification for the vendor
      await createNotification(vendorId, 'general', {
        type: isPast ? NOTIFICATION_TYPES.PAST_ASSOCIATED_VENDOR_REMOVED : NOTIFICATION_TYPES.ASSOCIATED_VENDOR_REMOVED,
        title: `${isPast ? 'Past ' : ''}Staff Association Removed`,
        message: `${userData.fullName} has removed you from their ${messagePrefix}associated vendors.`,
        userId: userData.userId,
        status: 'removed',
        timestamp: new Date().toISOString(),
        data: {
          _displayData: {
            fullName: userData.fullName,
            userType: userData.userType,
            userSubType: userData.userSubType
          }
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error sending removal notification:', error);
      return false;
    }
  };

  // Handle moving a vendor to past
  const handleMoveToPast = (vendorId) => {
    const vendor = displayedVendors.find(v => v.id === vendorId);
    if (!vendor) return;
    
    setSelectedVendorForMove(vendor);
    setIsMoveToPastModalVisible(true);
  };

  // Confirm moving a vendor to past
  const confirmMoveToPast = async () => {
    if (!selectedVendorForMove) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const vendor = selectedVendorForMove;
      
      // 1. Create duration from since date and current date
      const currentDate = new Date();
      const duration = {
        startMonth: vendor.since?.month || '1',
        startYear: vendor.since?.year || '2020',
        endMonth: (currentDate.getMonth() + 1).toString(),
        endYear: currentDate.getFullYear().toString()
      };
      
      // 2. Get the associatedVendors document
      const vendorRef = doc(db, 'users', userData.userId, 'associatedVendors', vendor.id);
      const vendorDoc = await getDoc(vendorRef);
      
      if (!vendorDoc.exists()) {
        throw new Error('Vendor not found in your associatedVendors collection');
      }
      
      // 3. Update the vendor document to mark as past
      await updateDoc(vendorRef, {
        isPast: true,
        duration: duration,
        movedToPastAt: new Date().toISOString()
      });
      
      // 4. Update counts and arrays in the main document
      const userRef = doc(db, 'users', userData.userId);
      
      // 4.1. Get updated top 3 current vendors
      const currentVendorRef = collection(db, 'users', userData.userId, 'associatedVendors');
      const currentTop3Query = query(
        currentVendorRef,
        where('isPast', '==', false),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const currentTop3Snapshot = await getDocs(currentTop3Query);
      const currentTop3Vendors = await Promise.all(
        currentTop3Snapshot.docs.map(async (docSnapshot) => {
          const vendorData = docSnapshot.data();
          
          // Enrich with user data
          if (vendorData.vendorId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  ...vendorData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${vendorData.vendorId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...vendorData
          };
        })
      );
      
      // 4.2. Get updated top 3 past vendors
      const pastVendorRef = collection(db, 'users', userData.userId, 'associatedVendors');
      const pastTop3Query = query(
        pastVendorRef,
        where('isPast', '==', true),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const pastTop3Snapshot = await getDocs(pastTop3Query);
      const pastTop3Vendors = await Promise.all(
        pastTop3Snapshot.docs.map(async (docSnapshot) => {
          const vendorData = docSnapshot.data();
          
          // Enrich with user data
          if (vendorData.vendorId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', vendorData.vendorId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  ...vendorData,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  idVerificationStatus: userData.idVerificationStatus || ''
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${vendorData.vendorId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...vendorData
          };
        })
      );
      
      // 4.3. Update main document with counts and arrays
      await updateDoc(userRef, {
        associatedVendors: currentTop3Vendors,
        pastAssociatedVendors: pastTop3Vendors,
        associatedVendorsCount: increment(-1),
        pastAssociatedVendorsCount: increment(1)
      });
      
      // 5. Update the other side of the relationship (vendor's vendorStaff)
      try {
        // Get the target ID (vendor userId)
        const targetId = vendor.vendorId;
        
        if (!targetId) throw new Error('Target ID not found');
        
        // Look up vendorStaff documents for this relationship
        const vendorStaffRef = collection(db, 'users', targetId, 'vendorStaff');
        const vendorStaffQuery = query(vendorStaffRef, where('userId', '==', userData.userId));
        
        const vendorStaffSnapshot = await getDocs(vendorStaffQuery);
        
        if (!vendorStaffSnapshot.empty) {
          // Update all matching entries
          for (const docSnap of vendorStaffSnapshot.docs) {
            await updateDoc(docSnap.ref, {
              isPast: true,
              duration: duration,
              movedToPastAt: new Date().toISOString()
            });
          }
          
          // Update the target's main document
          const vendorUserRef = doc(db, 'users', targetId);
          const vendorUserDoc = await getDoc(vendorUserRef);
          
          if (vendorUserDoc.exists()) {
            // Get new top 3 current vendorStaff
            const currentVendorStaffQuery = query(
              vendorStaffRef,
              where('isPast', '==', false),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const currentVendorStaffSnapshot = await getDocs(currentVendorStaffQuery);
            const top3CurrentVendorStaff = await Promise.all(
              currentVendorStaffSnapshot.docs.map(async (docSnapshot) => {
                const staffData = docSnapshot.data();
                
                // Enrich with staff data
                try {
                  const staffDoc = await getDoc(doc(db, 'users', staffData.userId));
                  if (staffDoc.exists()) {
                    const staffUserData = staffDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...staffData,
                      fullName: staffUserData.fullName || '',
                      userName: staffUserData.userName || '',
                      profilePictureUrl: staffUserData.profilePictureUrl || '',
                      userSubType: staffUserData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching staff data for ${staffData.userId}:`, error);
                }
                
                return {
                  id: docSnapshot.id,
                  ...staffData
                };
              })
            );
            
            // Get new top 3 past vendorStaff
            const pastVendorStaffQuery = query(
              vendorStaffRef,
              where('isPast', '==', true),
              orderBy('createdAt', 'desc'),
              limit(3)
            );
            
            const pastVendorStaffSnapshot = await getDocs(pastVendorStaffQuery);
            const top3PastVendorStaff = await Promise.all(
              pastVendorStaffSnapshot.docs.map(async (docSnapshot) => {
                const staffData = docSnapshot.data();
                
                // Enrich with staff data
                try {
                  const staffDoc = await getDoc(doc(db, 'users', staffData.userId));
                  if (staffDoc.exists()) {
                    const staffUserData = staffDoc.data();
                    return {
                      id: docSnapshot.id,
                      ...staffData,
                      fullName: staffUserData.fullName || '',
                      userName: staffUserData.userName || '',
                      profilePictureUrl: staffUserData.profilePictureUrl || '',
                      userSubType: staffUserData.userSubType || ''
                    };
                  }
                } catch (error) {
                  console.error(`Error fetching staff data for ${staffData.userId}:`, error);
                }
                
                return {
                  id: docSnapshot.id,
                  ...staffData
                };
              })
            );
            
            // Update target's document with updated top 3 arrays and counts
            await updateDoc(vendorUserRef, {
              vendorStaff: top3CurrentVendorStaff,
              pastVendorStaff: top3PastVendorStaff,
              vendorStaffCount: increment(-1),
              pastVendorStaffCount: increment(1)
            });
          }
        }
      
        // Create notification for the vendor
        await createNotification(targetId, 'general', {
          type: NOTIFICATION_TYPES.ASSOCIATED_VENDOR_MOVED_TO_PAST,
          title: 'Staff Relationship Moved to Past',
          message: `${userData.fullName || 'A staff member'} has moved you from their current vendors to past vendors.`,
          userId: userData.userId,
          status: 'updated',
          timestamp: new Date().toISOString(),
          data: {
            _displayData: {
              fullName: userData.fullName,
              userId: userData.userId,
              duration: duration
            }
          }
        });
      } catch (err) {
        console.error('Error updating vendor records:', err);
        // Continue even if this fails
      }
      
      // 6. Update UI state
      const updatedVendor = { 
        ...vendor, 
        isPast: true, 
        duration: duration,
        since: undefined // Remove since property since it's now a past vendor
      };
      setVendors(prev => prev.filter(v => v.id !== vendor.id));
      setDisplayedVendors(prev => prev.filter(v => v.id !== vendor.id));
      
      setPastVendors(prev => [updatedVendor, ...prev]);
      setDisplayedPastVendors(prev => [updatedVendor, ...prev]);
      
      // Close the modal
      setIsMoveToPastModalVisible(false);
      setSelectedVendorForMove(null);
      
      alert('Vendor successfully moved to past vendors');
    } catch (error) {
      console.error('Error moving vendor to past:', error);
      alert('Failed to move vendor to past. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle tour selection (for RightMenu)
  const handleTourSelect = (tour) => {
    // Navigate to tour or update state as needed
  };

  // Format duration for display
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    const { startMonth, startYear, endMonth, endYear } = duration;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `${months[parseInt(startMonth) - 1]} ${startYear} - ${months[parseInt(endMonth) - 1]} ${endYear}`;
  };

  // Format since date for display
  const formatSince = (since) => {
    if (!since) return '';
    
    const { month, year } = since;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return `Since ${months[parseInt(month) - 1]} ${year}`;
  };
  
  // Generate month options for dropdowns
  const generateMonthOptions = () => {
    return [
      { value: '1', label: 'January' },
      { value: '2', label: 'February' },
      { value: '3', label: 'March' },
      { value: '4', label: 'April' },
      { value: '5', label: 'May' },
      { value: '6', label: 'June' },
      { value: '7', label: 'July' },
      { value: '8', label: 'August' },
      { value: '9', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' }
    ];
  };

  // Generate year options for dropdowns
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 50; i--) {
      years.push({ value: i.toString(), label: i.toString() });
    }
    return years;
  };

  // Vendor Search Modal Component
  const VendorSearchModal = () => {
    if (!isVendorModalVisible) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsVendorModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          {!selectedVendor ? (
            // Search interface
            <>
              <div className={styles.modalHeader}>
                <h2>Add {activeTab === 'past' ? 'Past ' : ''}Vendor</h2>
                <button className={styles.closeButton} onClick={() => setIsVendorModalVisible(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className={styles.searchBarContainer}>
                <input
                  type="text"
                  placeholder="Search vendor by name..."
                  value={vendorSearchQuery}
                  onChange={(e) => setVendorSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                <button
                  className={styles.searchButton}
                  onClick={handleVendorSearch}
                  disabled={!vendorSearchQuery.trim() || searchingVendor}
                >
                  {searchingVendor ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                  <span>{searchingVendor ? 'Searching...' : 'Search'}</span>
                </button>
              </div>
              
              <div className={styles.searchResults}>
                {searchingVendor ? (
                  <div className={styles.noResults}>Searching...</div>
                ) : vendorResults.length > 0 ? (
                  vendorResults.map((vendor) => (
                    <div
                      key={vendor.id}
                      className={`${styles.searchResult} ${vendor.isAlreadyAdded ? styles.disabled : ''}`}
                      onClick={() => !vendor.isAlreadyAdded && handleInitialSelection(vendor)}
                    >
                      <img
                        src={vendor.profilePictureUrl || defaultUserImage}
                        alt={vendor.fullName}
                        className={styles.profilePhoto}
                      />
                      <div className={styles.userInfo}>
                        <div className={styles.nameContainer}>
                          <span className={styles.fullName}>{vendor.fullName}</span>
                          {vendor.idVerificationStatus === 'Verified' && (
                            <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                          )}
                        </div>
                        <span className={styles.userName}>@{vendor.userName}</span>
                        <span className={styles.userSubType}>{vendor.userSubType}</span>
                      </div>
                      {vendor.isAlreadyAdded && (
                        <span className={styles.alreadyAddedText}>Already Added</span>
                      )}
                    </div>
                  ))
                ) : vendorSearchQuery && !searchingVendor ? (
                  <div className={styles.noResults}>No vendor found</div>
                ) : null}
              </div>
            </>
          ) : (
            // Details and date selection for the selected vendor
            <>
              <div className={styles.modalHeader}>
                <h2>Add {selectedVendor.fullName} as {activeTab === 'past' ? 'Past ' : ''}Vendor</h2>
                <button className={styles.closeButton} onClick={() => setIsVendorModalVisible(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              <div className={styles.selectedVendorContainer}>
                <div className={styles.selectedVendorHeader}>Selected Vendor:</div>
                <div className={styles.selectedVendorDetails}>
                  <img 
                    src={selectedVendor.profilePictureUrl || defaultUserImage}
                    alt={selectedVendor.fullName}
                    className={styles.profilePhoto}
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.nameContainer}>
                      <span className={styles.fullName}>{selectedVendor.fullName}</span>
                      {selectedVendor.idVerificationStatus === 'Verified' && (
                        <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                      )}
                    </div>
                    <span className={styles.userName}>@{selectedVendor.userName}</span>
                    <span className={styles.userSubType}>{selectedVendor.userSubType}</span>
                  </div>
                </div>
                
                <button 
                  className={styles.backButton}
                  onClick={() => setSelectedVendor(null)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} /> Back to Search
                </button>
              </div>
              
              {/* Date Selection - only render what's appropriate for the current state */}
              <div className={styles.dateSelection}>
                <h3>{activeTab === 'past' ? 'Duration' : 'Since'}</h3>
                
                {activeTab === 'past' ? (
                  // Past Association - Start and End Dates
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Start Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={startMonth}
                          onChange={(e) => setStartMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={startYear}
                          onChange={(e) => setStartYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>End Date:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={endMonth}
                          onChange={(e) => setEndMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={endYear}
                          onChange={(e) => setEndYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Current Association - Since Date
                  <div className={styles.dateFields}>
                    <div className={styles.dateFieldGroup}>
                      <label className={styles.fieldLabel}>Since:</label>
                      <div className={styles.dateInputGroup}>
                        <select 
                          className={styles.dateSelect}
                          value={sinceMonth}
                          onChange={(e) => setSinceMonth(e.target.value)}
                          required
                        >
                          <option value="">Month</option>
                          {generateMonthOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        <select 
                          className={styles.dateSelect}
                          value={sinceYear}
                          onChange={(e) => setSinceYear(e.target.value)}
                          required
                        >
                          <option value="">Year</option>
                          {generateYearOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className={styles.formActions}>
                <button 
                  className={styles.addButton}
                  onClick={handleSubmitVendor}
                  disabled={(!sinceMonth && !startMonth)}
                >
                  {isProcessing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Move to Past Modal component
  const MoveToPastModal = () => {
    if (!isMoveToPastModalVisible || !selectedVendorForMove) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsMoveToPastModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Move to Past Vendors</h2>
            <button className={styles.closeButton} onClick={() => setIsMoveToPastModalVisible(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.moveVendorInfo}>
            <div className={styles.infoIcon}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </div>
            <p>
              You're about to move <strong>{selectedVendorForMove.fullName}</strong> from your current vendors to past vendors.
              This will update the relationship on both sides.
            </p>
            
            <div className={styles.dateInfo}>
              <div className={styles.dateItem}>
                <span className={styles.dateLabel}>From:</span>
                <span className={styles.dateValue}>
                  {selectedVendorForMove.since ? 
                    `${selectedVendorForMove.since.month}/${selectedVendorForMove.since.year}` :
                    'Not specified'}
                </span>
              </div>
              
              <div className={styles.dateArrow}>
                <FontAwesomeIcon icon={faArrowRight} />
              </div>
              
              <div className={styles.dateItem}>
                <span className={styles.dateLabel}>To:</span>
                <span className={styles.dateValue}>
                  {`${(new Date().getMonth() + 1)}/${new Date().getFullYear()}`}
                </span>
              </div>
            </div>
          </div>
          
          <div className={styles.modalActions}>
            <button 
              className={styles.cancelButton}
              onClick={() => setIsMoveToPastModalVisible(false)}
            >
              Cancel
            </button>
            
            <button 
              className={styles.confirmButton}
              onClick={confirmMoveToPast}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  <span>Confirm Move</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Function to render vendor permissions badges (only shown if vendor has assigned them)
  const renderPermissionBadges = (permissions) => {
    if (!permissions) return null;
    
    const badges = [];
    
    if (permissions.canAddItems) badges.push('Add Items');
    if (permissions.canEditItems) badges.push('Edit Items');
    if (permissions.canDeleteItems) badges.push('Delete Items');
    if (permissions.canChangeStatus) badges.push('Change Status');
    
    if (badges.length === 0) return <span className={styles.noPermissions}>No special permissions</span>;
    
    return (
      <div className={styles.permissionBadges}>
        {badges.map((badge, index) => (
          <span key={index} className={styles.permissionBadge}>{badge}</span>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.vendorContainer}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <main className={styles['vendor-main']}>
          <AuthCheckWrapper>
            {hasAccess ? (
              <>
                <div className={styles.pageHeader}>
                  <h1 className={styles.pageTitle}>Associated Vendors</h1>
                  <p className={styles.pageDescription}>
                    Add and manage vendors you've worked with.
                  </p>
                </div>
                
                {/* Tabs for Current/Past */}
                <div className={styles.vendorTabs}>
                  <button 
                    className={`${styles.vendorTab} ${activeTab === 'current' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('current')}
                  >
                    Current
                  </button>
                  <button 
                    className={`${styles.vendorTab} ${activeTab === 'past' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('past')}
                  >
                    Past
                  </button>
                </div>
                
                <div className={styles.vendorActionsSection}>
                  <div className={styles.searchContainer}>
                    <button 
                      className={styles.addVendorButton}
                      onClick={() => setIsVendorModalVisible(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <span>Add {activeTab === 'past' ? 'Past ' : ''}Vendor</span>
                    </button>
                  </div>
                </div>
                
                <div className={styles.vendorListContainer}>
                  <h2 className={styles.sectionTitle}>
                    Your {activeTab === 'past' ? 'Past ' : ''}Vendors
                  </h2>
                  
                  {(activeTab === 'current' && displayedVendors.length === 0) || 
                   (activeTab === 'past' && displayedPastVendors.length === 0) ? (
                    <div className={styles.emptyState}>
                      <FontAwesomeIcon icon={faBuilding} className={styles.emptyIcon} />
                      <p>You haven't added any {activeTab === 'past' ? 'past ' : ''}vendors yet</p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.vendorList}>
                        {(activeTab === 'current' ? displayedVendors : displayedPastVendors).map((member) => (
                          <div 
                            key={member.id} 
                            className={styles.vendorCard}
                          >
                            <div className={styles.vendorCardContent}>
                              <div 
                                className={styles.vendorInfo}
                                onClick={() => navigateToUserProfile(member.userName)}
                                style={{ cursor: 'pointer' }}
                              >
                                <img
                                  src={member.profilePictureUrl || defaultUserImage}
                                  alt={member.fullName}
                                  className={styles.vendorImage}
                                />
                                <div className={styles.vendorInfoText}>
                                  <div className={styles.vendorNameContainer}>
                                    <span className={styles.vendorName}>{member.fullName}</span>
                                    {member.idVerificationStatus === 'Verified' && (
                                      <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                                    )}
                                  </div>
                                  <span className={styles.vendorUsername}>@{member.userName}</span>
                                  <span className={styles.vendorType}>{member.userSubType}</span>
                                  
                                  {/* Permissions - only show if vendor has assigned them */}
                                  <div className={styles.vendorPermissions}>
                                    {renderPermissionBadges(member.permissions)}
                                  </div>
                                  
                                  {/* Since or Duration badge */}
                                  {activeTab === 'current' && member.since && (
                                    <div className={styles.sinceBadge}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                      <span>{formatSince(member.since)}</span>
                                    </div>
                                  )}
                                  
                                  {activeTab === 'past' && member.duration && (
                                    <div className={styles.durationBadge}>
                                      <FontAwesomeIcon icon={faCalendarAlt} />
                                      <span>{formatDuration(member.duration)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className={styles.vendorStatus}>
                                {member.status === 'pending' && (
                                  <span className={styles.pendingStatus}>Pending</span>
                                )}
                                {member.status === 'accepted' && (
                                  <span className={styles.acceptedStatus}>Active</span>
                                )}
                              </div>
                              
                              <div className={styles.vendorActions}>
                                {activeTab === 'current' && !member.isPending && member.status === 'accepted' && (
                                  <button
                                    className={styles.moveToPastButton}
                                    onClick={() => handleMoveToPast(member.id)}
                                    title="Move to past"
                                  >
                                    <FontAwesomeIcon icon={faArrowRight} />
                                  </button>
                                )}
                                <button
                                  className={styles.removeButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveVendor(member.id);
                                  }}
                                  title="Remove vendor"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Load more button */}
                      {((activeTab === 'current' && hasMoreVendors) || 
                        (activeTab === 'past' && hasMorePastVendors)) && (
                        <button 
                          className={styles.loadMoreButton}
                          onClick={loadMoreVendors}
                          disabled={isLoadingMore}
                        >
                          {isLoadingMore ? 'Loading...' : 'Load More'}
                        </button>
                      )}
                    </>
                  )}
                </div>
                
                {/* Search Modal */}
                {VendorSearchModal()}

                {/* Move to Past Modal */}
                {MoveToPastModal()}
                
                {/* Loading Overlay */}
                {isProcessing && (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}></div>
                    <div className={styles.loadingText}>
                      Processing...
                    </div>
                  </div>
                )}
              </>
            ) : (
              <PermissionDenied />
            )}
          </AuthCheckWrapper>
        </main>
        {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
      </div>
    </div>
  );
};

export default AssociatedVendors;